"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.SearchEngine = void 0;
const fuse_js_1 = __importDefault(require("fuse.js"));
const search_engine_types_1 = require("./search-engine-types");
function isNumberArray(val) {
    if (!Array.isArray(val)) {
        return false;
    }
    return val.every((v) => typeof v === 'number');
}
function isInRangeOp(op, v, cmp) {
    switch (op) {
        case '=':
            return v === cmp;
        case '<':
            return v < cmp;
        case '<=':
            return v <= cmp;
        case '>':
            return v > cmp;
        case '>=':
            return v >= cmp;
        default:
            console.error('did not handle op', op);
            return false;
    }
}
class SearchEngine {
    constructor(config) {
        const { idField, fields } = config;
        this.fuseInstances = {};
        this.indexRebuildPromise = null;
        this.currentSearchTerm = null;
        this.idField = idField;
        this.fields = fields;
    }
    getAllIndexableFields() {
        return Object.keys(this.fields).filter((fieldId) => !!this.fields[fieldId].searchConfig);
    }
    getAllCommands() {
        return Object.assign({}, this.fields);
    }
    commandCompletion(query, equal = false) {
        const commandMatch = query.match(search_engine_types_1.COMMAND_REGEX);
        const commandQuery = !commandMatch || commandMatch.length < 2
            ? null
            : commandMatch[1].toLowerCase();
        const strippedQuery = (commandQuery || query).trim().toLowerCase();
        const res = {};
        for (const key of Object.keys(this.fields)) {
            const field = this.fields[key];
            const command = field.command;
            if (command === null)
                continue;
            if (equal) {
                if (command !== commandQuery)
                    continue;
            }
            else {
                if (command === commandQuery)
                    continue;
            }
            if ((commandQuery && command.startsWith(commandQuery)) ||
                command.startsWith(strippedQuery))
                res[key] = field;
        }
        return res;
    }
    replaceCommand(query, newCommand) {
        const matches = query.match(search_engine_types_1.COMMAND_REGEX);
        if (!matches || matches.length < 2) {
            return `${newCommand}: `;
        }
        else {
            return `${newCommand}: ${matches[2]}`;
        }
    }
    filterNumericValsEntityIds(entities, query, field) {
        var _a, _b;
        const numericRangeOp = (_a = this.fields[field].searchConfig) === null || _a === void 0 ? void 0 : _a.numericRangeSearch;
        if (!numericRangeOp) {
            console.error('numeric filter called without range op. Unable to proceed. Returning empty results.');
            return [];
        }
        let entityIds = [];
        const indexTransformer = (_b = this.fields[field].searchConfig) === null || _b === void 0 ? void 0 : _b.indexTransformer;
        entities.forEach((v) => {
            const val = (indexTransformer === null || indexTransformer === void 0 ? void 0 : indexTransformer(v[field])) || v[field];
            if (val && typeof val !== 'number' && !isNumberArray(val)) {
                console.error(`Error: val (${val}) is not a numeric value nor number array. Entity: ${v}`);
                return;
            }
            const id = v[this.idField];
            if (typeof id !== 'number') {
                console.error(`Error: id (${id}) is not a numeric value. Entity: ${v}`);
                return;
            }
            if (val) {
                const cmp = Number(query.trim());
                let toAdd = false;
                if (typeof val === 'number') {
                    const valNum = val;
                    toAdd = isInRangeOp(numericRangeOp, valNum, cmp);
                }
                else if (isNumberArray(val)) {
                    const valNumArray = val;
                    toAdd = valNumArray.some((v) => isInRangeOp(numericRangeOp, v, cmp));
                }
                if (toAdd) {
                    entityIds.push(id);
                }
            }
        });
        return entityIds;
    }
    applyFilters(entities, filters) {
        if (!filters.length) {
            return entities;
        }
        console.time('Applying filters to entities');
        let filteredEntityIds = [];
        let fuseIndexes = filters.map((filter) => this.buildFuseIndex(entities, [filter.field]));
        filters.forEach((filter, index) => {
            var _a;
            let appliedEntityIds = ((_a = this.fields[filter.field].searchConfig) === null || _a === void 0 ? void 0 : _a.numericRangeSearch)
                ? this.filterNumericValsEntityIds(entities, filter.query, filter.field)
                : fuseIndexes[index]
                    .search(filter.query)
                    .map((result) => result.item.idField);
            if (index === 0)
                filteredEntityIds = appliedEntityIds;
            else
                filteredEntityIds = filteredEntityIds.filter((entityId) => appliedEntityIds.includes(entityId));
        });
        let filteredEntities = new Map();
        filteredEntityIds.forEach((entityId) => {
            let entity = entities.get(entityId);
            if (entity)
                filteredEntities.set(entityId, entity);
        });
        console.timeEnd('Applying filters to entities');
        console.log(`remaining entities after applying ${filters.length} filters:`, filteredEntities);
        return filteredEntities;
    }
    buildEntitiesIndex(entities, setSearchReady) {
        return new Promise((resolve, reject) => {
            try {
                if (entities.size < 1) {
                    console.log('Empty entities, not building index');
                    if (setSearchReady) {
                        setSearchReady();
                    }
                    return false;
                }
                console.time('Building index');
                this.getAllIndexableFields().forEach((indexedFieldId) => {
                    this.fuseInstances[indexedFieldId] = this.buildFuseIndex(entities, [
                        indexedFieldId,
                    ]);
                });
                console.timeEnd('Building index');
                if (setSearchReady) {
                    setSearchReady();
                }
                resolve(true);
                this.indexRebuildPromise = null;
            }
            catch (err) {
                console.error('Error building entities index - ', err);
                reject(err);
            }
        });
    }
    buildFuseIndex(entities, fields) {
        let indexList = Array.from(entities.keys())
            .map((entityId) => {
            let indexRow = {};
            indexRow.idField = entityId;
            const entity = entities.get(entityId);
            fields.forEach((indexedFieldId) => {
                var _a, _b;
                let entityField = this.fields[indexedFieldId];
                if ((_a = entityField.searchConfig) === null || _a === void 0 ? void 0 : _a.numericRangeSearch) {
                    return;
                }
                indexRow[indexedFieldId] = ((_b = entityField.searchConfig) === null || _b === void 0 ? void 0 : _b.indexTransformer)
                    ? entityField.searchConfig.indexTransformer(entity[indexedFieldId])
                    : entity[indexedFieldId];
            });
            return indexRow;
        })
            .filter((row) => row !== null);
        return new fuse_js_1.default(indexList, {
            keys: fields,
            includeMatches: true,
            isCaseSensitive: false,
            includeScore: true,
            shouldSort: true,
            useExtendedSearch: true,
            ignoreLocation: true,
            threshold: 0,
        });
    }
    async searchEntities(term, inpFieldIds = null, startAll = true) {
        try {
            if (this.indexRebuildPromise) {
                console.log('Index rebuilding - waiting...');
                this.currentSearchTerm = term;
                await this.indexRebuildPromise;
                if (this.currentSearchTerm !== term) {
                    console.log("Search has changed since we've waited. Aborting..");
                    return [];
                }
            }
            if (!term) {
                console.log('No search term specified');
                return [];
            }
            if (Object.keys(this.fuseInstances).length === 0) {
                return [];
            }
            let searches = [];
            const commandMatch = term.match(search_engine_types_1.COMMAND_REGEX);
            const command = (commandMatch === null || commandMatch === void 0 ? void 0 : commandMatch.length) ? commandMatch[1] : null;
            const query = !command || !commandMatch ? term : commandMatch[2];
            const fields = inpFieldIds
                ? inpFieldIds.filter((fieldId) => !!this.fields[fieldId].searchConfig)
                : this.getAllIndexableFields();
            fields.sort((a, b) => {
                var _a, _b;
                return (((_a = this.fields[a].searchConfig) === null || _a === void 0 ? void 0 : _a.searchPriority) || 999) -
                    (((_b = this.fields[b].searchConfig) === null || _b === void 0 ? void 0 : _b.searchPriority) || 999);
            });
            console.time('Search Dispatched');
            fields.forEach((searchFieldId) => {
                var _a, _b;
                const entityField = this.fields[searchFieldId];
                if (command && entityField.command !== command)
                    return;
                searches.push({
                    searchFieldName: searchFieldId,
                    searchFieldDescription: ((_a = entityField.searchConfig) === null || _a === void 0 ? void 0 : _a.searchFieldDescription) || '',
                    searchResultDescFn: entityField.countDescFunc,
                    filterLabel: entityField.countDescFunc(0, '', true),
                    priority: ((_b = entityField.searchConfig) === null || _b === void 0 ? void 0 : _b.searchPriority) || 0,
                    results: async () => {
                        let timeTaken = performance.now();
                        try {
                            let innerResults;
                            if (!this.fuseInstances[searchFieldId]) {
                                console.error('Fuse Individual instance missing for ', searchFieldId, ' - ', this.fuseInstances);
                                innerResults = [];
                            }
                            else {
                                const q = {};
                                let convertedQuery = query.trim();
                                if (entityField.searchType === 'prefix' && convertedQuery) {
                                    convertedQuery = `^${convertedQuery}`;
                                }
                                if (!convertedQuery) {
                                    innerResults = this.fuseInstances[searchFieldId]
                                        .getIndex()
                                        .docs.map((item, index) => ({
                                        item,
                                        refIndex: index,
                                        matches: [],
                                        score: 1,
                                    }));
                                }
                                else {
                                    q[searchFieldId] = convertedQuery;
                                    innerResults = this.fuseInstances[searchFieldId].search(q);
                                }
                            }
                            timeTaken = performance.now() - timeTaken;
                            return { results: innerResults, timeTaken };
                        }
                        catch (err) {
                            console.error('Error searching ', searchFieldId, ' - ', err);
                            return { results: [], timeTaken: performance.now() - timeTaken };
                        }
                    },
                });
                if (startAll)
                    searches[searches.length - 1].results();
            });
            console.timeEnd('Search Dispatched');
            return searches;
        }
        catch (err) {
            console.error('Error dispatching searches - ', err);
            return [];
        }
    }
}
exports.SearchEngine = SearchEngine;
